/**
 * MONSTER TOWN — RECORDED VOICE PLAYBACK. HUD/AUDIO/TOUCH CREW (src/audio/**).
 *
 * The clips are real recordings, rendered and whisper-verified by
 * `tools/voice/mt_generate.py` through the shared kids-arcade pipeline, and they
 * live in `public/voice/` as `<md5-12>.mp3` plus a `manifest.json` of
 * `game key -> file`. Same cast as Monster Quest, so the two games sound like
 * one world.
 *
 * FOUR RULES THIS FILE EXISTS TO KEEP:
 *
 *  1. NOTHING SPOKEN THAT WAS NEVER RECORDED. There is no synthesis fallback and
 *     no speechSynthesis anywhere. A key with no clip in the manifest is silence,
 *     and silence is fine — see rule 2.
 *  2. VOICE SUPPORTS PICTURES, NEVER REPLACES THEM (LAW #4). Nothing in the game
 *     waits on a clip, nothing is gated behind one, and no code path here can
 *     block a tap, a panel, a walk or a save. `voice-check` plays the whole game
 *     muted and asserts it is still completable.
 *  3. IT IS NEVER A NAG. One line at a time (a new line replaces the one
 *     playing), a floor between lines, and a long per-key cooldown, so a
 *     three-year-old drumming on the same prop hears the reaction sound every
 *     time and the sentence once.
 *  4. THE SPEAKER BUTTON IS THE MUTE. Voice hangs off the engine's `voice` bus,
 *     under master, so `full/quiet/off` already covers it — plus an explicit
 *     `setEnabled(false)` for anything that wants the world quiet without
 *     silencing the sound effects a kid is tapping.
 *
 * Clips are fetched and decoded ON DEMAND and cached; nothing is decoded at boot
 * (an older iPad does not need a hundred PCM buffers resident to hear "hello").
 * The service worker precaches `dist/voice/**` like every other built file, so
 * the second time round they come from the cache and offline still speaks.
 */

import { audio } from './engine'

/** Decoded clips kept in memory. Small; a line is ~1-3 s of mono 24 kHz. */
const MAX_CACHED = 48
/** Never two sentences at once, and never sentences back to back. */
const MIN_GAP_MS = 700
/** The same line will not repeat inside this window. */
const REPEAT_MS = 9000

type Manifest = Readonly<Record<string, string>>

let manifest: Manifest | null = null
let manifestPromise: Promise<Manifest> | null = null
let manifestFailed = false

const buffers = new Map<string, AudioBuffer>()
const pending = new Map<string, Promise<AudioBuffer | null>>()

let current: AudioBufferSourceNode | null = null
let lastPlayedAt = 0
const lastKeyAt = new Map<string, number>()
let enabled = true
/** Every key that was asked for, whether or not it had a clip. Test surface. */
const askedFor: string[] = []
let playedCount = 0
let missingCount = 0

function baseUrl(path: string): string {
  try {
    return new URL(path, document.baseURI).href
  } catch {
    return path
  }
}

/** Load `voice/manifest.json` once. A failure is permanent and silent. */
export function loadVoiceManifest(): Promise<Manifest> {
  if (manifest !== null) return Promise.resolve(manifest)
  if (manifestPromise !== null) return manifestPromise
  manifestPromise = fetch(baseUrl('voice/manifest.json'), { cache: 'default' })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
    .then((json: unknown) => {
      const out: Record<string, string> = {}
      if (json !== null && typeof json === 'object') {
        for (const [k, v] of Object.entries(json as Record<string, unknown>)) {
          if (typeof v === 'string' && v.length > 0) out[k] = v
        }
      }
      manifest = out
      return out
    })
    .catch(() => {
      // No voice pack on this deploy (or offline before the first fetch). The
      // game is unchanged: every line it would have spoken is already a picture.
      manifestFailed = true
      manifest = {}
      return manifest
    })
  return manifestPromise
}

function remember(key: string, buf: AudioBuffer): void {
  buffers.set(key, buf)
  if (buffers.size > MAX_CACHED) {
    const oldest = buffers.keys().next()
    if (oldest.done !== true) buffers.delete(oldest.value)
  }
}

async function fetchClip(key: string, file: string): Promise<AudioBuffer | null> {
  const ctx = audio.context
  if (ctx === null) return null
  try {
    const res = await fetch(baseUrl(`voice/${file}`), { cache: 'default' })
    if (!res.ok) return null
    const bytes = await res.arrayBuffer()
    const buf = await ctx.decodeAudioData(bytes)
    remember(key, buf)
    return buf
  } catch {
    return null
  } finally {
    pending.delete(key)
  }
}

function clipFor(key: string): Promise<AudioBuffer | null> {
  const cached = buffers.get(key)
  if (cached !== undefined) return Promise.resolve(cached)
  const already = pending.get(key)
  if (already !== undefined) return already
  const m = manifest
  if (m === null) return loadVoiceManifest().then(() => clipFor(key))
  const file = m[key]
  if (file === undefined) return Promise.resolve(null)
  const p = fetchClip(key, file)
  pending.set(key, p)
  return p
}

/** Stop whatever is talking. Used when a new line starts and by `setEnabled`. */
export function stopVoice(): void {
  if (current === null) return
  try {
    current.stop()
  } catch {
    /* already finished */
  }
  try {
    current.disconnect()
  } catch {
    /* already gone */
  }
  current = null
}

/**
 * Say a line. Returns immediately; the caller never learns whether a clip
 * existed and must never care (LAW #4).
 */
export function playVoice(key: string): void {
  askedFor.push(key)
  if (askedFor.length > 400) askedFor.shift()
  if (!enabled) return
  if (audio.volumeMode === 'off') return
  const now = performance.now()
  if (now - lastPlayedAt < MIN_GAP_MS) return
  const seen = lastKeyAt.get(key)
  if (seen !== undefined && now - seen < REPEAT_MS) return
  const buses = audio.ensure()
  const ctx = audio.context
  if (buses === null || ctx === null || ctx.state !== 'running') return

  lastKeyAt.set(key, now)
  void clipFor(key).then((buf) => {
    if (buf === null) {
      missingCount++
      return
    }
    if (!enabled || audio.volumeMode === 'off') return
    const c = audio.context
    const b = audio.ensure()
    if (c === null || b === null || c.state !== 'running') return
    stopVoice()
    const src = c.createBufferSource()
    src.buffer = buf
    src.connect(b.voice)
    src.start(c.currentTime + 0.01)
    src.onended = (): void => {
      if (current === src) current = null
    }
    current = src
    lastPlayedAt = performance.now()
    playedCount++
    // Pull the bed down under the sentence, then let it back up.
    audio.duck(buf.duration + 0.25)
  })
}

/** The mute path that is not the speaker button. */
export function setVoiceEnabled(on: boolean): void {
  enabled = on
  if (!on) stopVoice()
}

export function voiceEnabled(): boolean {
  return enabled
}

export interface VoiceTestApi {
  readonly ready: boolean
  /** Keys the manifest actually has clips for. */
  keys(): string[]
  has(key: string): boolean
  /** Every key the game asked for this session, in order. */
  asked(): string[]
  say(key: string): void
  stop(): void
  enabled(): boolean
  setEnabled(on: boolean): void
  stats(): { played: number; missing: number; asked: number; manifest: number; failed: boolean }
  /** Resolves once the manifest has been fetched (or failed). */
  ready$(): Promise<number>
}

export function installVoicePlayback(): void {
  void loadVoiceManifest()
  const api: VoiceTestApi = {
    ready: true,
    keys: () => Object.keys(manifest ?? {}),
    has: (key) => (manifest ?? {})[key] !== undefined,
    asked: () => [...askedFor],
    say: (key) => playVoice(key),
    stop: () => stopVoice(),
    enabled: () => enabled,
    setEnabled: (on) => setVoiceEnabled(on),
    stats: () => ({
      played: playedCount,
      missing: missingCount,
      asked: askedFor.length,
      manifest: Object.keys(manifest ?? {}).length,
      failed: manifestFailed,
    }),
    ready$: () => loadVoiceManifest().then((m) => Object.keys(m).length),
  }
  window.__voice = api
}

declare global {
  interface Window {
    __voice?: VoiceTestApi
  }
}
